import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createBreeding } from "../../../api/breeding";
import { I18nKey } from "../../../i18n/i18n";
import { Button } from "../../common/Button";
import { ContextSummary } from "../../common/ContextSummary";
import { Modal, ModalActions } from "../../common/Modal";
import { sectionToIcon } from "../../type/default";
import { UnattachedDevices } from "../device/UnattachedDevices";
import { AddBuildingForm } from "../setup/AddBuildingForm";
import { AssociateDeviceForm } from "../setup/AssociateDeviceForm";
import { SetupError } from "../setup/SetupLayout";
import { useBreedingBuildings } from "../setup/useBreedingBuildings";
import {
  BreedingFields,
  EMPTY_BREEDING_FORM,
  useBreedingForm,
} from "./BreedingFields";

type Step = "breeding" | "buildings" | "devices";

const STEP_LABELS: Record<Step, I18nKey> = {
  breeding: "setup-step-breeding",
  buildings: "setup-step-buildings",
  devices: "setup-step-devices",
};

type CreateBreedingModalProps = {
  companyId: number;
  companyName: string;
  /** Comptes de l'entreprise, rappelés pour que le PSC sache chez qui il crée. */
  accountNames: string[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * Création d'un élevage par un admin / PSC chez une entreprise cliente, sur le modèle de
 * la mise en route de l'éleveur : l'élevage, puis des bâtiments facultatifs, puis les
 * produits. L'élevage existe dès la première étape : fermer ensuite le conserve.
 */
export const CreateBreedingModal = ({
  companyId,
  companyName,
  accountNames,
  isOpen,
  onClose,
  onSuccess,
}: CreateBreedingModalProps) => {
  const { t } = useTranslation();
  const breedingForm = useBreedingForm(EMPTY_BREEDING_FORM);
  const { reset } = breedingForm;
  const [step, setStep] = useState<Step>("breeding");
  const [breedingId, setBreedingId] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    reset(EMPTY_BREEDING_FORM);
    setStep("breeding");
    setBreedingId(0);
    setError(null);
  }, [isOpen, reset]);

  const close = () => {
    onClose();
    if (breedingId) onSuccess();
  };

  const submitBreeding = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!breedingForm.validate()) return;

    setSubmitting(true);
    const result = await createBreeding({
      ...breedingForm.form,
      country: "FR",
      id_company: companyId,
    });
    setSubmitting(false);

    if (!result.success || !result.breedingId) {
      setError((result.error && t(result.error)) || t("setup-error-breeding"));
      return;
    }
    setBreedingId(result.breedingId);
    setStep("buildings");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t("breeding-create")}
      icon={sectionToIcon.section_breeding}
      maxWidth="md"
    >
      <div className="space-y-4">
        <ContextSummary
          rows={[
            {
              label: t("breeding-create-for-account"),
              value: accountNames.join(", "),
            },
            { label: t("breeding-create-for-company"), value: companyName },
          ]}
        />

        <h3 className="font-oxaniumMedium text-tertiary">
          {t(STEP_LABELS[step])}
        </h3>

        {step === "breeding" && (
          <form onSubmit={submitBreeding} className="space-y-4">
            <SetupError message={error} />
            <BreedingFields breedingForm={breedingForm} />
            <ModalActions>
              <Button type="button" variant="secondary" onClick={close}>
                {t("button-cancel")}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? t("setup-saving") : t("setup-next")}
              </Button>
            </ModalActions>
          </form>
        )}

        {step !== "breeding" && (
          <BreedingContentSteps
            step={step}
            breedingId={breedingId}
            companyId={companyId}
            onNext={() => setStep("devices")}
            onFinish={close}
          />
        )}
      </div>
    </Modal>
  );
};

/** Étapes qui suivent la création : l'élevage existe, ses bâtiments peuvent être lus. */
const BreedingContentSteps = ({
  step,
  breedingId,
  companyId,
  onNext,
  onFinish,
}: {
  step: "buildings" | "devices";
  breedingId: number;
  companyId: number;
  onNext: () => void;
  onFinish: () => void;
}) => {
  const { t } = useTranslation();
  const { buildings, reload } = useBreedingBuildings(breedingId);
  const [associated, setAssociated] = useState<string[]>([]);

  if (step === "buildings") {
    return (
      <>
        <p className="text-sm text-grey-readable">
          {t("breeding-create-buildings-optional")}
        </p>
        <AddBuildingForm
          breedingId={breedingId}
          buildings={buildings}
          onAdded={reload}
        />
        <ModalActions>
          {/* Sans bâtiment, pas d'étape produits : l'élevage est créé seul. */}
          {buildings.length > 0 ? (
            <Button type="button" onClick={onNext}>
              {t("setup-next")}
            </Button>
          ) : (
            <Button type="button" onClick={onFinish}>
              {t("breeding-create-finish-without-building")}
            </Button>
          )}
        </ModalActions>
      </>
    );
  }

  return (
    <>
      <UnattachedDevices companyId={companyId} buildings={buildings} />
      <AssociateDeviceForm
        companyId={companyId}
        buildings={buildings}
        associated={associated}
        onAssociated={(uuid) => setAssociated((prev) => [...prev, uuid])}
      />
      <ModalActions>
        <Button type="button" onClick={onFinish}>
          {t("button-close")}
        </Button>
      </ModalActions>
    </>
  );
};
